import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Settings2,
  Shield,
  Bell,
  Receipt,
  RotateCcw,
  Save,
  Upload,
  Trash2,
  Plus,
  Users,
  FileText,
  Download,
  Send,
  AlertTriangle,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AdminSettings,
  DEFAULT_SETTINGS,
  Exemption,
  GeneralSettings,
  NotificationsSettings,
  Role,
  SecuritySettings,
  TaxSettings,
  VatRate,
  loadSettings,
  saveSettings,
  clearSettings,
} from "@/lib/admin-settings-store";

export const Route = createFileRoute("/admin/settings")({
  component: Page,
});

const TABS = [
  { key: "general", icon: Settings2 },
  { key: "security", icon: Shield },
  { key: "notifications", icon: Bell },
  { key: "tax", icon: Receipt },
] as const;

function Page() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("general");
  const [saved, setSaved] = useState<AdminSettings>(() => loadSettings());
  const [draft, setDraft] = useState<AdminSettings>(saved);

  useEffect(() => {
    const s = loadSettings();
    setSaved(s);
    setDraft(s);
  }, []);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);

  const saveAll = () => {
    saveSettings(draft);
    setSaved(draft);
    toast.success(t("adminSettings.savedToast"));
  };
  const discard = () => {
    setDraft(saved);
    toast(t("adminSettings.discardToast"));
  };
  const resetDefaults = () => {
    clearSettings();
    setDraft(DEFAULT_SETTINGS);
    setSaved(DEFAULT_SETTINGS);
    toast.success(t("adminSettings.resetToast"));
  };

  const patch = <K extends keyof AdminSettings>(key: K, value: Partial<AdminSettings[K]>) => {
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...value } }));
  };

  return (
    <AdminShell
      title={t("pages.admin.settings.title")}
      subtitle={t("pages.admin.settings.subtitle")}
      actions={
        <>
          {dirty && (
            <Badge variant="secondary" className="rounded-full bg-amber-500/15 text-amber-700">
              {t("adminSettings.unsaved")}
            </Badge>
          )}
          <Button variant="outline" className="rounded-xl" onClick={resetDefaults}>
            <RotateCcw className="h-4 w-4" /> {t("adminSettings.reset")}
          </Button>
          <Button
            disabled={!dirty}
            className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90 disabled:opacity-60"
            onClick={saveAll}
          >
            <Save className="h-4 w-4" /> {t("adminSettings.save")}
          </Button>
        </>
      }
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList className="mb-6 h-auto flex-wrap gap-1 rounded-xl bg-muted p-1">
          {TABS.map((it) => (
            <TabsTrigger
              key={it.key}
              value={it.key}
              className="gap-2 rounded-lg px-4 py-2"
            >
              <it.icon className="h-4 w-4" />
              {t(`adminSettings.tabs.${it.key}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general" className="mt-0">
          <GeneralTab
            value={draft.general}
            onChange={(v) => patch("general", v)}
            dirty={dirty}
            onSave={saveAll}
            onDiscard={discard}
          />
        </TabsContent>
        <TabsContent value="security" className="mt-0">
          <SecurityTab
            value={draft.security}
            onChange={(v) => patch("security", v)}
            dirty={dirty}
            onSave={saveAll}
            onDiscard={discard}
          />
        </TabsContent>
        <TabsContent value="notifications" className="mt-0">
          <NotificationsTab
            value={draft.notifications}
            onChange={(v) => patch("notifications", v)}
            dirty={dirty}
            onSave={saveAll}
            onDiscard={discard}
          />
        </TabsContent>
        <TabsContent value="tax" className="mt-0">
          <TaxTab
            value={draft.tax}
            onChange={(v) => patch("tax", v)}
            dirty={dirty}
            onSave={saveAll}
            onDiscard={discard}
          />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}

/* -------------------- shared UI -------------------- */

function SectionCard({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </Card>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className ?? ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function Footer({
  note,
  dirty,
  onSave,
  onDiscard,
  extra,
}: {
  note: string;
  dirty: boolean;
  onSave: () => void;
  onDiscard: () => void;
  extra?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-background p-4 shadow-soft">
      <div className="flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-[#5958F3]/10 text-xs font-semibold text-[#5958F3]">
          OA
        </div>
        <div>
          <p className="text-xs font-semibold">{t("adminSettings.footerUser")}</p>
          <p className="text-[11px] text-muted-foreground">
            {dirty ? t("adminSettings.unsavedNote") : note}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" disabled={!dirty} className="rounded-xl" onClick={onDiscard}>
          {t("adminSettings.discard")}
        </Button>
        {extra}
        <Button
          size="sm"
          disabled={!dirty}
          className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90 disabled:opacity-60"
          onClick={onSave}
        >
          <Save className="h-4 w-4" /> {t("adminSettings.saveAll")}
        </Button>
      </div>
    </div>
  );
}

type TabProps<T> = {
  value: T;
  onChange: (v: Partial<T>) => void;
  dirty: boolean;
  onSave: () => void;
  onDiscard: () => void;
};

/* -------------------- GENERAL -------------------- */

function GeneralTab({ value, onChange, dirty, onSave, onDiscard }: TabProps<GeneralSettings>) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const set = <K extends keyof GeneralSettings>(k: K, v: GeneralSettings[K]) => onChange({ [k]: v } as Partial<GeneralSettings>);

  const handleLogo = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("adminSettings.general.logoTooLarge"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      set("logo", reader.result as string);
      toast.success(t("adminSettings.general.logoUploaded"));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6">
      <SectionCard title={t("adminSettings.general.identity")} description={t("adminSettings.general.identityDesc")}>
        <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-dashed border-border/70 p-4">
          <div className="grid h-16 w-40 place-items-center overflow-hidden rounded-lg bg-[#5958F3]/10 text-xs font-semibold text-[#5958F3]">
            {value.logo ? (
              <img src={value.logo} alt="Logo" className="h-full w-full object-contain" />
            ) : (
              t("adminSettings.general.logoPlaceholder")
            )}
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-xs text-muted-foreground">{t("adminSettings.general.logoHint")}</p>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/svg+xml,image/webp,image/jpeg"
              className="hidden"
              onChange={(e) => handleLogo(e.target.files?.[0])}
            />
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => fileInput.current?.click()}>
              <Upload className="h-4 w-4" /> {t("adminSettings.general.uploadLogo")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!value.logo}
              className="rounded-xl text-destructive disabled:opacity-50"
              onClick={() => {
                set("logo", null);
                toast.success(t("adminSettings.general.logoRemoved"));
              }}
            >
              <Trash2 className="h-4 w-4" /> {t("adminSettings.general.remove")}
            </Button>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminSettings.general.platformName")}>
            <Input value={value.platformName} onChange={(e) => set("platformName", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.tagline")}>
            <Input value={value.tagline} onChange={(e) => set("tagline", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.supportEmail")}>
            <Input type="email" value={value.supportEmail} onChange={(e) => set("supportEmail", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.businessEmail")}>
            <Input type="email" value={value.businessEmail} onChange={(e) => set("businessEmail", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.website")}>
            <Input value={value.website} onChange={(e) => set("website", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.supportPhone")}>
            <Input value={value.supportPhone} onChange={(e) => set("supportPhone", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.description")} className="sm:col-span-2">
            <Textarea rows={3} value={value.description} onChange={(e) => set("description", e.target.value)} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title={t("adminSettings.general.regional")} description={t("adminSettings.general.regionalDesc")}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("adminSettings.general.timezone")}>
            <Select value={value.timezone} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="riyadh">Asia/Riyadh (UTC+3)</SelectItem>
                <SelectItem value="dubai">Asia/Dubai (UTC+4)</SelectItem>
                <SelectItem value="utc">UTC</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.general.currency")}>
            <Select value={value.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sar">{t("adminSettings.general.sar")}</SelectItem>
                <SelectItem value="aed">{t("adminSettings.general.aed")}</SelectItem>
                <SelectItem value="usd">{t("adminSettings.general.usd")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.general.language")}>
            <Select value={value.language} onValueChange={(v) => set("language", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{t("adminSettings.general.english")}</SelectItem>
                <SelectItem value="ar">{t("adminSettings.general.arabic")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.general.weekStart")}>
            <Select value={value.weekStart} onValueChange={(v) => set("weekStart", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sun">{t("adminSettings.general.sunday")}</SelectItem>
                <SelectItem value="mon">{t("adminSettings.general.monday")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </SectionCard>

      <SectionCard title={t("adminSettings.general.booking")} description={t("adminSettings.general.bookingDesc")}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t("adminSettings.general.cancellationWindow")}>
            <Input value={value.cancellationWindow} onChange={(e) => set("cancellationWindow", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.commissionRate")}>
            <Input value={value.commissionRate} onChange={(e) => set("commissionRate", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.minBookingDuration")}>
            <Input value={value.minBookingDuration} onChange={(e) => set("minBookingDuration", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.maxAdvanceBooking")}>
            <Input value={value.maxAdvanceBooking} onChange={(e) => set("maxAdvanceBooking", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.payoutCycle")}>
            <Input value={value.payoutCycle} onChange={(e) => set("payoutCycle", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.minPayoutThreshold")}>
            <Input value={value.minPayoutThreshold} onChange={(e) => set("minPayoutThreshold", e.target.value)} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title={t("adminSettings.general.social")} description={t("adminSettings.general.socialDesc")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminSettings.general.instagram")}>
            <Input value={value.instagram} onChange={(e) => set("instagram", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.twitter")}>
            <Input value={value.twitter} onChange={(e) => set("twitter", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.linkedin")}>
            <Input value={value.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.general.facebook")}>
            <Input value={value.facebook} onChange={(e) => set("facebook", e.target.value)} />
          </Field>
        </div>
      </SectionCard>

      <Footer
        dirty={dirty}
        onSave={onSave}
        onDiscard={onDiscard}
        note={t("adminSettings.general.note")}
      />
    </div>
  );
}

/* -------------------- SECURITY -------------------- */

function SecurityTab({ value, onChange, dirty, onSave, onDiscard }: TabProps<SecuritySettings>) {
  const { t } = useTranslation();
  const roleName = (r: Role) => t(`adminSettings.security.roleNames.${r.id}`, { defaultValue: r.name });
  const [createOpen, setCreateOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", desc: "" });

  const selected = value.roles.find((r) => r.id === value.selectedRoleId) ?? value.roles[0];

  const togglePerm = (permId: string, enabled: boolean) => {
    const rp = { ...(value.rolePermissions[selected.id] ?? {}), [permId]: enabled };
    onChange({ rolePermissions: { ...value.rolePermissions, [selected.id]: rp } });
  };

  const createRole = () => {
    if (!newRole.name.trim()) {
      toast.error(t("adminSettings.security.roleNameRequired"));
      return;
    }
    const id = newRole.name.toLowerCase().replace(/\s+/g, "-") + "-" + Date.now();
    const role: Role = { id, name: newRole.name.trim(), users: 0, desc: newRole.desc.trim() || t("adminSettings.security.customRole") };
    const perms = Object.fromEntries(value.permissions.map((p) => [p.id, false]));
    onChange({
      roles: [...value.roles, role],
      rolePermissions: { ...value.rolePermissions, [id]: perms },
      selectedRoleId: id,
    });
    setNewRole({ name: "", desc: "" });
    setCreateOpen(false);
    toast.success(t("adminSettings.security.roleCreated", { name: role.name }));
  };

  const deleteRole = (id: string) => {
    if (value.roles.length <= 1) {
      toast.error(t("adminSettings.security.atLeastOne"));
      return;
    }
    const roles = value.roles.filter((r) => r.id !== id);
    const rp = { ...value.rolePermissions };
    delete rp[id];
    onChange({
      roles,
      rolePermissions: rp,
      selectedRoleId: value.selectedRoleId === id ? roles[0].id : value.selectedRoleId,
    });
    toast.success(t("adminSettings.security.roleDeleted"));
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title={t("adminSettings.security.roles")}
        description={t("adminSettings.security.rolesDesc")}
        actions={
          <Button
            size="sm"
            className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4" /> {t("adminSettings.security.createRole")}
          </Button>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {value.roles.map((r) => (
            <div
              key={r.id}
              className={`group relative cursor-pointer rounded-xl border p-4 text-start transition ${
                value.selectedRoleId === r.id ? "border-[#5958F3] bg-[#5958F3]/5" : "border-border/60 hover:bg-muted/50"
              }`}
              onClick={() => onChange({ selectedRoleId: r.id })}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-semibold">{roleName(r)}</span>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                    <Users className="h-3 w-3" /> {r.users} {t("adminSettings.security.users")}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRole(r.id);
                    }}
                    className="rounded-md p-1 text-muted-foreground opacity-0 transition hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                    aria-label={t("adminSettings.security.deleteRole")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t(`adminSettings.security.roleDescs.${r.id}`, { defaultValue: r.desc })}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title={t("adminSettings.security.permissions")}
        description={t("adminSettings.security.permissionsDesc", { role: roleName(selected) })}
      >
        <div className="space-y-3">
          {value.permissions.map((p) => {
            const on = value.rolePermissions[selected.id]?.[p.id] ?? false;
            return (
              <div key={p.id} className="flex items-center justify-between rounded-xl border border-border/60 p-3">
                <div>
                  <p className="text-sm font-medium">{t(`adminSettings.security.permNames.${p.id}`, { defaultValue: p.title })}</p>
                  <p className="text-xs text-muted-foreground">{t(`adminSettings.security.permDescs.${p.id}`, { defaultValue: p.desc })}</p>
                </div>
                <Switch checked={on} onCheckedChange={(v) => togglePerm(p.id, v)} />
              </div>
            );
          })}
        </div>
      </SectionCard>

      <Footer
        dirty={dirty}
        onSave={onSave}
        onDiscard={onDiscard}
        note={t("adminSettings.security.note")}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("adminSettings.security.createTitle")}</DialogTitle>
            <DialogDescription>{t("adminSettings.security.createDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label={t("adminSettings.security.roleName")}>
              <Input value={newRole.name} onChange={(e) => setNewRole((s) => ({ ...s, name: e.target.value }))} placeholder={t("adminSettings.security.roleNamePh")} />
            </Field>
            <Field label={t("adminSettings.security.descLabel")}>
              <Textarea rows={3} value={newRole.desc} onChange={(e) => setNewRole((s) => ({ ...s, desc: e.target.value }))} placeholder={t("adminSettings.security.descPh")} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>{t("adminSettings.security.cancel")}</Button>
            <Button className="bg-[#5958F3] text-white hover:bg-[#5958F3]/90" onClick={createRole}>
              {t("adminSettings.security.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------- NOTIFICATIONS -------------------- */

const CHANNELS = [
  { key: "email" as const, provider: "SendGrid" },
  { key: "sms" as const, provider: "Twilio" },
  { key: "push" as const, provider: "Firebase FCM" },
];

function EventTable({
  events,
  onChange,
}: {
  events: Record<string, { email: boolean; sms: boolean; push: boolean }>;
  onChange: (v: Record<string, { email: boolean; sms: boolean; push: boolean }>) => void;
}) {
  const { t } = useTranslation();
  const setEv = (key: string, channel: "email" | "sms" | "push", v: boolean) => {
    onChange({ ...events, [key]: { ...events[key], [channel]: v } });
  };
  const allOn = (key: string) => events[key].email && events[key].sms && events[key].push;
  const setAll = (key: string, v: boolean) => onChange({ ...events, [key]: { email: v, sms: v, push: v } });

  return (
    <div className="overflow-x-auto rounded-xl border border-border/60">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("adminSettings.notifications.event")}</TableHead>
            <TableHead className="text-center">{t("adminSettings.notifications.email")}</TableHead>
            <TableHead className="text-center">{t("adminSettings.notifications.sms")}</TableHead>
            <TableHead className="text-center">{t("adminSettings.notifications.pushShort")}</TableHead>
            <TableHead className="text-center">{t("adminSettings.notifications.all")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.keys(events).map((e) => (
            <TableRow key={e}>
              <TableCell className="font-medium">{t(`adminSettings.notifications.events.${e}`, { defaultValue: e })}</TableCell>
              <TableCell className="text-center">
                <Checkbox checked={events[e].email} onCheckedChange={(v) => setEv(e, "email", !!v)} className="mx-auto" />
              </TableCell>
              <TableCell className="text-center">
                <Checkbox checked={events[e].sms} onCheckedChange={(v) => setEv(e, "sms", !!v)} className="mx-auto" />
              </TableCell>
              <TableCell className="text-center">
                <Checkbox checked={events[e].push} onCheckedChange={(v) => setEv(e, "push", !!v)} className="mx-auto" />
              </TableCell>
              <TableCell className="text-center">
                <Checkbox checked={allOn(e)} onCheckedChange={(v) => setAll(e, !!v)} className="mx-auto" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function NotificationsTab({ value, onChange, dirty, onSave, onDiscard }: TabProps<NotificationsSettings>) {
  const { t } = useTranslation();
  const set = <K extends keyof NotificationsSettings>(k: K, v: NotificationsSettings[K]) =>
    onChange({ [k]: v } as Partial<NotificationsSettings>);

  const sendTest = () => {
    const enabled = Object.entries(value.channels).filter(([, v]) => v.enabled).map(([k]) => k);
    if (enabled.length === 0) {
      toast.error(t("adminSettings.notifications.enableOne"));
      return;
    }
    toast.success(
      t("adminSettings.notifications.testSent", {
        channels: enabled.map((k) => t(`adminSettings.notifications.${k}`)).join(", "),
      }),
    );
  };

  return (
    <div className="space-y-6">
      <SectionCard title={t("adminSettings.notifications.channels")} description={t("adminSettings.notifications.channelsDesc")}>
        <div className="grid gap-3 sm:grid-cols-3">
          {CHANNELS.map((c) => (
            <div key={c.key} className="rounded-xl border border-border/60 p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold">{t(`adminSettings.notifications.${c.key}`)}</span>
                <Switch
                  checked={value.channels[c.key].enabled}
                  onCheckedChange={(v) =>
                    set("channels", { ...value.channels, [c.key]: { enabled: v } })
                  }
                />
              </div>
              <p className="mb-2 text-xs text-muted-foreground">{t(`adminSettings.notifications.${c.key}Desc`)}</p>
              <Badge variant="secondary" className="text-[10px]">{c.provider}</Badge>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={t("adminSettings.notifications.bookingTitle")} description={t("adminSettings.notifications.bookingDesc")}>
        <EventTable events={value.bookingEvents} onChange={(v) => set("bookingEvents", v)} />
      </SectionCard>

      <SectionCard title={t("adminSettings.notifications.financeTitle")} description={t("adminSettings.notifications.financeDesc")}>
        <EventTable events={value.financeEvents} onChange={(v) => set("financeEvents", v)} />
      </SectionCard>

      <SectionCard title={t("adminSettings.notifications.adminTitle")} description={t("adminSettings.notifications.adminDesc")}>
        <div className="space-y-3">
          {Object.entries(value.adminAlerts).map(([title, prefs]) => (
            <div
              key={title}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 p-3"
            >
              <div className="min-w-[240px] flex-1">
                <p className="text-sm font-medium">{t(`adminSettings.notifications.events.${title}`, { defaultValue: title })}</p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={prefs.email}
                    onCheckedChange={(v) =>
                      set("adminAlerts", { ...value.adminAlerts, [title]: { ...prefs, email: !!v } })
                    }
                  /> {t("adminSettings.notifications.email")}
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={prefs.sms}
                    onCheckedChange={(v) =>
                      set("adminAlerts", { ...value.adminAlerts, [title]: { ...prefs, sms: !!v } })
                    }
                  /> {t("adminSettings.notifications.sms")}
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={prefs.push}
                    onCheckedChange={(v) =>
                      set("adminAlerts", { ...value.adminAlerts, [title]: { ...prefs, push: !!v } })
                    }
                  /> {t("adminSettings.notifications.pushShort")}
                </label>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title={t("adminSettings.notifications.providerTitle")}
        description={t("adminSettings.notifications.providerDesc")}
        actions={
          <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">
            <Check className="h-3 w-3" /> {t("adminSettings.notifications.connected")}
          </Badge>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminSettings.notifications.provider")}>
            <Select value={value.emailProvider} onValueChange={(v) => set("emailProvider", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sendgrid">SendGrid</SelectItem>
                <SelectItem value="ses">Amazon SES</SelectItem>
                <SelectItem value="mailgun">Mailgun</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.notifications.fromEmail")}>
            <Input value={value.fromEmail} onChange={(e) => set("fromEmail", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.notifications.fromName")}>
            <Input value={value.fromName} onChange={(e) => set("fromName", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.notifications.apiKey")}>
            <Input type="password" value={value.apiKey} onChange={(e) => set("apiKey", e.target.value)} />
          </Field>
        </div>
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
            <div>
              <p className="text-sm font-medium">{t("adminSettings.notifications.openTracking")}</p>
              <p className="text-xs text-muted-foreground">{t("adminSettings.notifications.openTrackingDesc")}</p>
            </div>
            <Switch checked={value.openTracking} onCheckedChange={(v) => set("openTracking", v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
            <div>
              <p className="text-sm font-medium">{t("adminSettings.notifications.unsubscribe")}</p>
              <p className="text-xs text-muted-foreground">{t("adminSettings.notifications.unsubscribeDesc")}</p>
            </div>
            <Switch checked={value.unsubscribeFooter} onCheckedChange={(v) => set("unsubscribeFooter", v)} />
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t("adminSettings.notifications.digestTitle")} description={t("adminSettings.notifications.digestDesc")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminSettings.notifications.digestFrequency")}>
            <Select value={value.digestFrequency} onValueChange={(v) => set("digestFrequency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="realtime">{t("adminSettings.notifications.realtime")}</SelectItem>
                <SelectItem value="daily">{t("adminSettings.notifications.daily")}</SelectItem>
                <SelectItem value="weekly">{t("adminSettings.notifications.weekly")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.notifications.digestSendTime")}>
            <Input value={value.digestSendTime} onChange={(e) => set("digestSendTime", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.notifications.quietHours")}>
            <Input value={value.quietHours} onChange={(e) => set("quietHours", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.notifications.maxPerDay")}>
            <Input value={value.maxPerDay} onChange={(e) => set("maxPerDay", e.target.value)} />
          </Field>
        </div>
      </SectionCard>

      <Footer
        dirty={dirty}
        onSave={onSave}
        onDiscard={onDiscard}
        note={t("adminSettings.notifications.note")}
        extra={
          <Button size="sm" variant="outline" className="rounded-xl" onClick={sendTest}>
            <Send className="h-4 w-4" /> {t("adminSettings.notifications.sendTest")}
          </Button>
        }
      />
    </div>
  );
}

/* -------------------- TAX -------------------- */

const REPORTS = [
  { key: "q1", status: "Filed" },
  { key: "q4", status: "Filed" },
  { key: "q2", status: "Upcoming" },
];

function TaxTab({ value, onChange, dirty, onSave, onDiscard }: TabProps<TaxSettings>) {
  const { t } = useTranslation();
  const tStatus = (s: string) => t(`adminSettings.tax.statuses.${s}`, { defaultValue: s });
  const set = <K extends keyof TaxSettings>(k: K, v: TaxSettings[K]) =>
    onChange({ [k]: v } as Partial<TaxSettings>);

  const [rateOpen, setRateOpen] = useState(false);
  const [newRate, setNewRate] = useState<Omit<VatRate, "id">>({ category: "", region: "", rate: "", status: "Active" });
  const [exOpen, setExOpen] = useState(false);
  const [newEx, setNewEx] = useState<Omit<Exemption, "id">>({ name: "", note: "", status: "Exempt" });

  const addRate = () => {
    if (!newRate.category || !newRate.region || !newRate.rate) {
      toast.error(t("adminSettings.tax.fillRate"));
      return;
    }
    set("rates", [...value.rates, { ...newRate, id: `r-${Date.now()}` }]);
    setNewRate({ category: "", region: "", rate: "", status: "Active" });
    setRateOpen(false);
    toast.success(t("adminSettings.tax.rateAdded"));
  };
  const removeRate = (id: string) => {
    set("rates", value.rates.filter((r) => r.id !== id));
    toast.success(t("adminSettings.tax.rateRemoved"));
  };
  const addEx = () => {
    if (!newEx.name) {
      toast.error(t("adminSettings.tax.nameRequired"));
      return;
    }
    set("exemptions", [...value.exemptions, { ...newEx, id: `e-${Date.now()}` }]);
    setNewEx({ name: "", note: "", status: "Exempt" });
    setExOpen(false);
    toast.success(t("adminSettings.tax.exemptionAdded"));
  };
  const removeEx = (id: string) => {
    set("exemptions", value.exemptions.filter((e) => e.id !== id));
    toast.success(t("adminSettings.tax.exemptionRemoved"));
  };

  const exportReport = (title: string, detail: string) => {
    const csv = `Title,Detail\n"${title}","${detail}"\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("adminSettings.tax.exported", { title }));
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="font-semibold">{t("adminSettings.tax.noticeTitle")}</p>
          <p className="text-xs text-muted-foreground">{t("adminSettings.tax.noticeDesc")}</p>
        </div>
      </div>

      <SectionCard title={t("adminSettings.tax.globalTitle")} description={t("adminSettings.tax.globalDesc")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminSettings.tax.regime")}>
            <Select value={value.regime} onValueChange={(v) => set("regime", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vat">{t("adminSettings.tax.vat")}</SelectItem>
                <SelectItem value="gst">{t("adminSettings.tax.gst")}</SelectItem>
                <SelectItem value="sales">{t("adminSettings.tax.sales")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.tax.baseCurrency")}>
            <Select value={value.baseCurrency} onValueChange={(v) => set("baseCurrency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sar">{t("adminSettings.tax.sar")}</SelectItem>
                <SelectItem value="aed">{t("adminSettings.tax.aed")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.tax.registrationNumber")}>
            <Input value={value.registrationNumber} onChange={(e) => set("registrationNumber", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.tax.taxYearStart")}>
            <Input value={value.taxYearStart} onChange={(e) => set("taxYearStart", e.target.value)} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title={t("adminSettings.tax.invoiceTitle")} description={t("adminSettings.tax.invoiceDesc")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminSettings.tax.invoicePrefix")}>
            <Input value={value.invoicePrefix} onChange={(e) => set("invoicePrefix", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.tax.receiptPrefix")}>
            <Input value={value.receiptPrefix} onChange={(e) => set("receiptPrefix", e.target.value)} />
          </Field>
          <Field label={t("adminSettings.tax.invoiceFooter")} className="sm:col-span-2">
            <Textarea rows={2} value={value.invoiceFooter} onChange={(e) => set("invoiceFooter", e.target.value)} />
          </Field>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {(["showTaxOnInvoices", "inclusiveOfTax", "merchantTaxCollection", "qrOnInvoices"] as const).map((key) => (
            <div key={key} className="flex items-center justify-between rounded-xl border border-border/60 p-3">
              <p className="text-sm font-medium">{t(`adminSettings.tax.toggles.${key}`)}</p>
              <Switch
                checked={value.toggles[key]}
                onCheckedChange={(v) => set("toggles", { ...value.toggles, [key]: v })}
              />
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title={t("adminSettings.tax.ratesTitle")}
        actions={
          <Button
            size="sm"
            className="rounded-xl bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
            onClick={() => setRateOpen(true)}
          >
            <Plus className="h-4 w-4" /> {t("adminSettings.tax.addRate")}
          </Button>
        }
      >
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminSettings.tax.category")}</TableHead>
                <TableHead>{t("adminSettings.tax.region")}</TableHead>
                <TableHead>{t("adminSettings.tax.rate")}</TableHead>
                <TableHead>{t("adminSettings.tax.status")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {value.rates.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{t(`adminSettings.tax.categories.${r.category}`, { defaultValue: r.category })}</TableCell>
                  <TableCell>{t(`adminSettings.tax.regions.${r.region}`, { defaultValue: r.region })}</TableCell>
                  <TableCell>{r.rate}</TableCell>
                  <TableCell>
                    <Badge
                      variant={r.status === "Active" ? "default" : "secondary"}
                      className={r.status === "Active" ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15" : ""}
                    >
                      {tStatus(r.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-end">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeRate(r.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <SectionCard
        title={t("adminSettings.tax.exemptionsTitle")}
        actions={
          <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setExOpen(true)}>
            <Plus className="h-4 w-4" /> {t("adminSettings.tax.addExemption")}
          </Button>
        }
      >
        <div className="space-y-2">
          {value.exemptions.map((e) => (
            <div key={e.id} className="flex items-center justify-between rounded-xl border border-border/60 p-3">
              <div>
                <p className="text-sm font-medium">{e.name}</p>
                <p className="text-xs text-muted-foreground">{e.note}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className={e.status === "Exempt" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}
                >
                  {tStatus(e.status)}
                </Badge>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeEx(e.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={t("adminSettings.tax.reportingTitle")} description={t("adminSettings.tax.reportingDesc")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("adminSettings.tax.reportingFrequency")}>
            <Select value={value.reportingFrequency} onValueChange={(v) => set("reportingFrequency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">{t("adminSettings.tax.monthly")}</SelectItem>
                <SelectItem value="quarterly">{t("adminSettings.tax.quarterly")}</SelectItem>
                <SelectItem value="yearly">{t("adminSettings.tax.yearly")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("adminSettings.tax.nextFilingDate")}>
            <Input value={value.nextFilingDate} onChange={(e) => set("nextFilingDate", e.target.value)} />
          </Field>
        </div>
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("adminSettings.tax.recentReports")}</p>
          <div className="space-y-2">
            {REPORTS.map((r) => {
              const title = t(`adminSettings.tax.reports.${r.key}.title`);
              const detail = t(`adminSettings.tax.reports.${r.key}.detail`);
              return (
              <div key={r.key} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 p-3">
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#5958F3]/10 text-[#5958F3]">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-medium">{title}</p>
                    <p className="text-xs text-muted-foreground">{detail}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className={r.status === "Filed" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}
                  >
                    {tStatus(r.status)}
                  </Badge>
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={() => exportReport(title, detail)}>
                    <Download className="h-4 w-4" />{" "}
                    {r.status === "Filed" ? t("adminSettings.tax.export") : t("adminSettings.tax.draft")}
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-border/60 p-3">
          <div>
            <p className="text-sm font-medium">{t("adminSettings.tax.autoNotify")}</p>
            <p className="text-xs text-muted-foreground">{t("adminSettings.tax.autoNotifyDesc")}</p>
          </div>
          <Switch checked={value.autoNotify} onCheckedChange={(v) => set("autoNotify", v)} />
        </div>
      </SectionCard>

      <Footer dirty={dirty} onSave={onSave} onDiscard={onDiscard} note={t("adminSettings.tax.note")} />

      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("adminSettings.tax.addRateTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("adminSettings.tax.category")}>
              <Input value={newRate.category} onChange={(e) => setNewRate((s) => ({ ...s, category: e.target.value }))} />
            </Field>
            <Field label={t("adminSettings.tax.region")}>
              <Input value={newRate.region} onChange={(e) => setNewRate((s) => ({ ...s, region: e.target.value }))} />
            </Field>
            <Field label={t("adminSettings.tax.rate")}>
              <Input placeholder="15%" value={newRate.rate} onChange={(e) => setNewRate((s) => ({ ...s, rate: e.target.value }))} />
            </Field>
            <Field label={t("adminSettings.tax.status")}>
              <Select value={newRate.status} onValueChange={(v) => setNewRate((s) => ({ ...s, status: v as VatRate["status"] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">{tStatus("Active")}</SelectItem>
                  <SelectItem value="Exempt">{tStatus("Exempt")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRateOpen(false)}>{t("adminSettings.tax.cancel")}</Button>
            <Button className="bg-[#5958F3] text-white hover:bg-[#5958F3]/90" onClick={addRate}>{t("adminSettings.tax.addRateBtn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={exOpen} onOpenChange={setExOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("adminSettings.tax.addExemptionTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label={t("adminSettings.tax.entityName")}>
              <Input value={newEx.name} onChange={(e) => setNewEx((s) => ({ ...s, name: e.target.value }))} />
            </Field>
            <Field label={t("adminSettings.tax.noteField")}>
              <Input value={newEx.note} onChange={(e) => setNewEx((s) => ({ ...s, note: e.target.value }))} />
            </Field>
            <Field label={t("adminSettings.tax.status")}>
              <Select value={newEx.status} onValueChange={(v) => setNewEx((s) => ({ ...s, status: v as Exemption["status"] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Exempt">{tStatus("Exempt")}</SelectItem>
                  <SelectItem value="Pending">{tStatus("Pending")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExOpen(false)}>{t("adminSettings.tax.cancel")}</Button>
            <Button className="bg-[#5958F3] text-white hover:bg-[#5958F3]/90" onClick={addEx}>{t("adminSettings.tax.addExemptionBtn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
